// setup stream: setup wizard, guided demo, settings rework, connection states.
export const en: Record<string, string> = {
  "state.demo": "Demo",
  "state.not_connected": "Not connected",
  "state.ready_for_setup": "Ready for setup",
  "state.test": "Test",
  "state.live": "Live",

  "setup.title": "Agency setup",
  "setup.subtitle": "A few minutes now, and the workspace is yours. You can come back to any step later.",
  "setup.skip": "Skip for now",
  "setup.back": "Back",
  "setup.next": "Save and continue",
  "setup.saveExit": "Save and exit",
  "setup.finish": "Finish",
  "setup.stepOf": "Step {n} of 5",
  "setup.saved": "Saved",
  "setup.step.identity": "Agency",
  "setup.step.profile": "Business",
  "setup.step.connections": "Connections",
  "setup.step.data": "Starting data",
  "setup.step.finish": "Finish",

  "setup.identity.title": "Who is this agency?",
  "setup.identity.sub": "Use your real agency or a fictional one for the demo. Leave anything you do not know blank.",
  "setup.identity.legalName": "Legal name",
  "setup.identity.dba": "DBA (optional)",
  "setup.identity.displayName": "Name shown in the app",
  "setup.identity.logo": "Logo",
  "setup.identity.logoHelp": "PNG, JPEG or SVG up to 500 KB.",
  "setup.identity.logoPick": "Choose logo",
  "setup.identity.logoUploaded": "Logo uploaded",
  "setup.identity.address": "Office address",
  "setup.identity.line1": "Street",
  "setup.identity.line2": "Suite (optional)",
  "setup.identity.city": "City",
  "setup.identity.state": "State",
  "setup.identity.zip": "ZIP",
  "setup.identity.phone": "Business phone",
  "setup.identity.email": "Business email",
  "setup.identity.website": "Website",
  "setup.identity.timezone": "Time zone",
  "setup.identity.locale": "Preferred language",
  "setup.identity.ids": "Identifiers (optional)",
  "setup.identity.idsHelp": "Only if you have them handy. Nothing is verified against a licensing service, and none of this blocks the demo.",
  "setup.identity.ein": "EIN / FEIN",
  "setup.identity.npn": "Business-entity NPN",
  "setup.identity.licenses": "State agency licenses",
  "setup.identity.licenseNumber": "License number",
  "setup.identity.licenseKind": "Type (optional)",
  "setup.identity.addLicense": "Add license",
  "setup.identity.remove": "Remove",
  "setup.identity.fictional": "This is a fictional demo agency",
  "setup.identity.producerNote": "Individual producer NPNs and licenses go on each staff member in Settings → Staff.",

  "setup.profile.title": "What does the agency write?",
  "setup.profile.sub": "This shapes the sample book, the forms list and the carrier workspace.",
  "setup.profile.states": "States served",
  "setup.profile.addState": "Add a state",
  "setup.profile.lines": "Lines of business",
  "setup.profile.products": "Products offered",
  "setup.profile.productPlaceholder": "e.g. Non-standard auto, Mobile home, Bonds",
  "setup.profile.add": "Add",
  "setup.profile.carriers": "Carriers and MGAs",
  "setup.profile.carrierName": "Carrier or MGA name",
  "setup.profile.carrierKind": "Type",
  "setup.profile.kind.carrier": "Carrier",
  "setup.profile.kind.mga": "MGA",
  "setup.profile.carrierLines": "Lines",
  "setup.profile.addCarrier": "Add carrier",
  "setup.profile.currentSystem": "Current management system",
  "setup.profile.forms": "Forms you use most",
  "setup.profile.workflows": "Workflows you want to try",
  "setup.profile.workflow.certificates": "Certificates and evidence of insurance",
  "setup.profile.workflow.commercial_apps": "Commercial applications (125 stack)",
  "setup.profile.workflow.renewals": "Renewal follow-up",
  "setup.profile.workflow.texting": "Two-way texting",
  "setup.profile.workflow.carrier_downloads": "Carrier downloads and servicing",
  "setup.profile.workflow.import": "Migration from the current system",
  "setup.profile.workflow.faraday": "Faraday assistant",
  "setup.profile.workflow.quoting": "Comparative quoting",

  "setup.connections.title": "Connections",
  "setup.connections.sub": "Pick what you intend to use. Choosing a provider configures it; it is not connected or purchased until activation.",
  "setup.connections.forms": "ACORD forms provider",
  "setup.connections.forms.formcruise": "FormCruise (WinsurTech)",
  "setup.connections.forms.formcruiseNote": "Documented API; dedicated endpoints for 25/27/35/50. The agency holds its own ACORD end-user license.",
  "setup.connections.forms.simply_easier": "Simply Easier ACORD Forms",
  "setup.connections.forms.simply_easierNote": "Fallback option; API details to be confirmed with the vendor.",
  "setup.connections.forms.undecided": "Decide later",
  "setup.connections.forms.undecidedNote": "Demo preparation documents work either way.",
  "setup.connections.configured": "Configured — not connected",
  "setup.connections.texting": "Texting",
  "setup.connections.texting.wanted": "We want two-way texting",
  "setup.connections.texting.sender": "Send from",
  "setup.connections.texting.sender.agency_number": "The agency's own registered number (10DLC / toll-free registration under the agency)",
  "setup.connections.texting.sender.demo_number": "The 10x demo number for now (labeled as such)",
  "setup.connections.texting.sender.undecided": "Decide later",
  "setup.connections.texting.allowlist": "During the demo, only text allow-listed participants",
  "setup.connections.carrier": "How should carrier data reach us?",
  "setup.connections.carrier.ivans": "IVANS downloads (AL3)",
  "setup.connections.carrier.api": "Carrier APIs where available",
  "setup.connections.carrier.exported_files": "Exported files we upload",
  "setup.connections.carrier.portal_automation": "Authorized portal automation (Faraday executor)",
  "setup.connections.carrier.document_ingestion": "Documents and email ingestion",
  "setup.connections.carrier.undecided": "Decide later",
  "setup.connections.states": "What is connected right now",
  "setup.connections.statesSub": "Server-side truth. Demo means sample behavior behind the same interface the real provider will use.",

  "setup.data.title": "Starting data",
  "setup.data.sub": "You can change your mind later. Sample records stay separate from anything you enter.",
  "setup.data.sample": "Load a sample book",
  "setup.data.sampleSub": "A fictional Texas book: customers, policies, renewals, tasks and messages. Everything is marked sample and can be removed in one click.",
  "setup.data.sampleLoaded": "Sample book loaded",
  "setup.data.import": "Import a customer / policy export",
  "setup.data.importSub": "Upload a CSV from your current system. Preview rows, map columns, validate, then import. Opens after you finish.",
  "setup.data.importChosen": "We will open Import after you finish",
  "setup.data.empty": "Start empty",
  "setup.data.emptySub": "No sample records. Add customers yourself or import later.",
  "setup.data.emptyChosen": "Starting empty",
  "setup.data.choose": "Choose",
  "setup.data.chosen": "Chosen",

  "setup.finish.title": "You're set",
  "setup.finish.sub": "Here is what we have. Everything can be changed in Settings.",
  "setup.finish.agency": "Agency",
  "setup.finish.lines": "Lines",
  "setup.finish.carriers": "Carriers / MGAs",
  "setup.finish.forms": "Forms provider",
  "setup.finish.texting": "Texting",
  "setup.finish.data": "Starting data",
  "setup.finish.open": "Open the workspace",
  "setup.finish.guide": "Start the guided demo",
  "setup.finish.none": "Not set",
  "setup.finish.texting.on": "Wanted",
  "setup.finish.texting.off": "Not now",

  "guide.title": "Guided demo",
  "guide.subtitle": "The principal walkthrough, start to finish. Each step opens the screen where it happens.",
  "guide.progress": "{done} of {total} done",
  "guide.open": "Open",
  "guide.markDone": "Mark done",
  "guide.undo": "Undo",
  "guide.doneAt": "Done",
  "guide.step.setup": "Sign in and complete agency setup",
  "guide.step.setup.sub": "Identity, business profile, connections and starting data. Resumable at any time.",
  "guide.step.import": "Import a sample customer / policy file",
  "guide.step.import.sub": "Upload → preview rows → map columns → validate → review → import. Two differently structured sample exports are bundled.",
  "guide.step.customer": "Open an imported customer",
  "guide.step.customer.sub": "Search by name, phone or policy number; see policies, documents, conversation and tasks on one page.",
  "guide.step.faraday": "Ask Faraday to prepare a form and list what is missing",
  "guide.step.faraday.sub": "In the Faraday panel, type: prepare 25 for <customer name>. Faraday starts the draft and reports unanswered questions.",
  "guide.step.message": "Exchange a test message",
  "guide.step.message.sub": "Live text to an allow-listed participant when texting is connected; otherwise a clearly labeled simulated conversation.",
  "guide.step.answer": "Save the supplied answer",
  "guide.step.answer.sub": "Put the customer's answer on the form draft. Unknown stays unknown; nothing is invented.",
  "guide.step.pdf": "Generate, preview and download the preparation PDF",
  "guide.step.pdf.sub": "An original agency-branded document marked DEMO, saved to the customer's document history.",
  "guide.step.carrier": "Run a demonstration carrier response",
  "guide.step.carrier.sub": "The simulator asks for a missing piece of information, then produces a sample policy file.",
  "guide.step.carrierImport": "Process that file through the real import pipeline",
  "guide.step.carrierImport.sub": "Same mapping and validation as any upload; policy changes and follow-up tasks appear in the CRM.",
  "guide.step.review": "Show the updated customer, document, conversation and task",
  "guide.step.review.sub": "Everything Faraday or the simulator did is on the customer page with a receipt.",
  "guide.step.persist": "Refresh and confirm the work persists",
  "guide.step.persist.sub": "Reload the browser or sign in again. Records live on the server, not in the tab.",
  "guide.explore": "Free exploration",
  "guide.exploreSub": "Everything else is open. Come back here any time from the sidebar.",
  "guide.tip": "Tip",

  "settings.agency": "Agency",
  "settings.agency.sub": "What the prospect entered during setup.",
  "settings.agency.edit": "Edit in setup",
  "settings.agency.notSet": "Not set",
  "settings.agency.fictional": "Fictional demo agency",
  "settings.state": "State",
  "settings.configured": "Configured",
  "settings.running": "Running",
  "settings.staff": "Staff and producers",
  "settings.staff.sub": "Roles, languages, producer NPNs and state licenses live here, not on the agency.",
  "settings.staff.add": "Add staff member",
  "settings.staff.edit": "Edit",
  "settings.staff.name": "Name",
  "settings.staff.email": "Email",
  "settings.staff.role": "Role",
  "settings.staff.title": "Title",
  "settings.staff.phone": "Phone",
  "settings.staff.npn": "Producer NPN",
  "settings.staff.licenses": "State licenses",
  "settings.staff.active": "Active",
  "settings.staff.inactive": "Inactive",
  "settings.staff.saved": "Staff member saved",
  "settings.activation": "Activation",
  "settings.activation.sub": "Tell 10x which connections you want turned on. This records interest and the remaining setup items; it buys nothing and activates nothing.",
  "settings.activation.request": "Request activation",
  "settings.activation.items": "Connections to activate",
  "settings.activation.notes": "Notes",
  "settings.activation.contact": "Your name",
  "settings.activation.contactEmail": "Email",
  "settings.activation.contactPhone": "Phone",
  "settings.activation.sent": "Activation request recorded",
  "settings.activation.past": "Requests",
  "settings.activation.remaining": "Remaining setup",
  "settings.activation.none": "No requests yet.",
  "settings.demoData": "Demo data",
  "settings.demoData.sub": "Sample records are tagged and kept apart from anything you enter. Removing or reloading them never touches your own customers, messages or documents.",
  "settings.demoData.loaded": "Sample book loaded",
  "settings.demoData.notLoaded": "No sample book loaded",
  "settings.demoData.counts": "{customers} customers ({sample} sample) · {policies} policies",
  "settings.demoData.reload": "Reload sample book",
  "settings.demoData.remove": "Remove sample records",
  "settings.demoData.done": "Sample data updated",
  "settings.importLink": "Import customers and policies",
  "settings.importLink.sub": "Upload an export from your current system, map the columns, validate and import.",
  "settings.importLink.open": "Open Import",
};

export const es: Record<string, string> = {
  "state.demo": "Demo",
  "state.not_connected": "No conectado",
  "state.ready_for_setup": "Listo para configurar",
  "state.test": "Prueba",
  "state.live": "En vivo",

  "setup.title": "Configuración de la agencia",
  "setup.subtitle": "Unos minutos ahora y el espacio de trabajo es suyo. Puede volver a cualquier paso después.",
  "setup.skip": "Omitir por ahora",
  "setup.back": "Atrás",
  "setup.next": "Guardar y continuar",
  "setup.saveExit": "Guardar y salir",
  "setup.finish": "Terminar",
  "setup.stepOf": "Paso {n} de 5",
  "setup.saved": "Guardado",
  "setup.step.identity": "Agencia",
  "setup.step.profile": "Negocio",
  "setup.step.connections": "Conexiones",
  "setup.step.data": "Datos iniciales",
  "setup.step.finish": "Terminar",

  "setup.identity.title": "¿Qué agencia es esta?",
  "setup.identity.sub": "Use su agencia real o una ficticia para la demostración. Deje en blanco lo que no sepa.",
  "setup.identity.legalName": "Razón social",
  "setup.identity.dba": "Nombre comercial (opcional)",
  "setup.identity.displayName": "Nombre que se muestra en la app",
  "setup.identity.logo": "Logotipo",
  "setup.identity.logoHelp": "PNG, JPEG o SVG de hasta 500 KB.",
  "setup.identity.logoPick": "Elegir logotipo",
  "setup.identity.logoUploaded": "Logotipo subido",
  "setup.identity.address": "Dirección de la oficina",
  "setup.identity.line1": "Calle",
  "setup.identity.line2": "Suite (opcional)",
  "setup.identity.city": "Ciudad",
  "setup.identity.state": "Estado",
  "setup.identity.zip": "Código postal",
  "setup.identity.phone": "Teléfono del negocio",
  "setup.identity.email": "Correo del negocio",
  "setup.identity.website": "Sitio web",
  "setup.identity.timezone": "Zona horaria",
  "setup.identity.locale": "Idioma preferido",
  "setup.identity.ids": "Identificadores (opcional)",
  "setup.identity.idsHelp": "Solo si los tiene a la mano. No se verifica nada con ningún servicio de licencias y nada de esto bloquea la demostración.",
  "setup.identity.ein": "EIN / FEIN",
  "setup.identity.npn": "NPN de la entidad",
  "setup.identity.licenses": "Licencias estatales de la agencia",
  "setup.identity.licenseNumber": "Número de licencia",
  "setup.identity.licenseKind": "Tipo (opcional)",
  "setup.identity.addLicense": "Agregar licencia",
  "setup.identity.remove": "Quitar",
  "setup.identity.fictional": "Esta es una agencia ficticia de demostración",
  "setup.identity.producerNote": "Los NPN y licencias de cada productor van en cada miembro del personal en Ajustes → Personal.",

  "setup.profile.title": "¿Qué escribe la agencia?",
  "setup.profile.sub": "Esto da forma al libro de muestra, la lista de formularios y el espacio de aseguradoras.",
  "setup.profile.states": "Estados atendidos",
  "setup.profile.addState": "Agregar estado",
  "setup.profile.lines": "Líneas de negocio",
  "setup.profile.products": "Productos ofrecidos",
  "setup.profile.productPlaceholder": "p. ej. Auto no estándar, Casa móvil, Fianzas",
  "setup.profile.add": "Agregar",
  "setup.profile.carriers": "Aseguradoras y MGAs",
  "setup.profile.carrierName": "Nombre de la aseguradora o MGA",
  "setup.profile.carrierKind": "Tipo",
  "setup.profile.kind.carrier": "Aseguradora",
  "setup.profile.kind.mga": "MGA",
  "setup.profile.carrierLines": "Líneas",
  "setup.profile.addCarrier": "Agregar aseguradora",
  "setup.profile.currentSystem": "Sistema de administración actual",
  "setup.profile.forms": "Formularios que más usa",
  "setup.profile.workflows": "Flujos que quiere probar",
  "setup.profile.workflow.certificates": "Certificados y evidencia de seguro",
  "setup.profile.workflow.commercial_apps": "Solicitudes comerciales (paquete 125)",
  "setup.profile.workflow.renewals": "Seguimiento de renovaciones",
  "setup.profile.workflow.texting": "Mensajes de texto bidireccionales",
  "setup.profile.workflow.carrier_downloads": "Descargas y servicio con aseguradoras",
  "setup.profile.workflow.import": "Migración desde el sistema actual",
  "setup.profile.workflow.faraday": "Asistente Faraday",
  "setup.profile.workflow.quoting": "Cotización comparativa",

  "setup.connections.title": "Conexiones",
  "setup.connections.sub": "Elija lo que piensa usar. Elegir un proveedor lo configura; no queda conectado ni comprado hasta la activación.",
  "setup.connections.forms": "Proveedor de formularios ACORD",
  "setup.connections.forms.formcruise": "FormCruise (WinsurTech)",
  "setup.connections.forms.formcruiseNote": "API documentada; endpoints dedicados para 25/27/35/50. La agencia tiene su propia licencia ACORD.",
  "setup.connections.forms.simply_easier": "Simply Easier ACORD Forms",
  "setup.connections.forms.simply_easierNote": "Opción alterna; detalles de la API por confirmar con el proveedor.",
  "setup.connections.forms.undecided": "Decidir después",
  "setup.connections.forms.undecidedNote": "Los documentos de preparación de demostración funcionan de cualquier modo.",
  "setup.connections.configured": "Configurado — no conectado",
  "setup.connections.texting": "Mensajes de texto",
  "setup.connections.texting.wanted": "Queremos mensajes de texto bidireccionales",
  "setup.connections.texting.sender": "Enviar desde",
  "setup.connections.texting.sender.agency_number": "El número registrado de la agencia (registro 10DLC / toll-free a nombre de la agencia)",
  "setup.connections.texting.sender.demo_number": "El número de demostración de 10x por ahora (identificado como tal)",
  "setup.connections.texting.sender.undecided": "Decidir después",
  "setup.connections.texting.allowlist": "Durante la demo, solo enviar textos a participantes en la lista permitida",
  "setup.connections.carrier": "¿Cómo deben llegarnos los datos de las aseguradoras?",
  "setup.connections.carrier.ivans": "Descargas IVANS (AL3)",
  "setup.connections.carrier.api": "APIs de aseguradoras donde existan",
  "setup.connections.carrier.exported_files": "Archivos exportados que subimos",
  "setup.connections.carrier.portal_automation": "Automatización autorizada de portal (ejecutor Faraday)",
  "setup.connections.carrier.document_ingestion": "Ingesta de documentos y correo",
  "setup.connections.carrier.undecided": "Decidir después",
  "setup.connections.states": "Qué está conectado ahora mismo",
  "setup.connections.statesSub": "Verdad del servidor. Demo significa comportamiento de muestra detrás de la misma interfaz que usará el proveedor real.",

  "setup.data.title": "Datos iniciales",
  "setup.data.sub": "Puede cambiar de opinión después. Los registros de muestra se mantienen separados de lo que usted capture.",
  "setup.data.sample": "Cargar un libro de muestra",
  "setup.data.sampleSub": "Un libro ficticio de Texas: clientes, pólizas, renovaciones, tareas y mensajes. Todo está marcado como muestra y se quita con un clic.",
  "setup.data.sampleLoaded": "Libro de muestra cargado",
  "setup.data.import": "Importar una exportación de clientes / pólizas",
  "setup.data.importSub": "Suba un CSV de su sistema actual. Vea las filas, asigne columnas, valide e importe. Se abre al terminar.",
  "setup.data.importChosen": "Abriremos Importar cuando termine",
  "setup.data.empty": "Empezar vacío",
  "setup.data.emptySub": "Sin registros de muestra. Agregue clientes o importe después.",
  "setup.data.emptyChosen": "Empezando vacío",
  "setup.data.choose": "Elegir",
  "setup.data.chosen": "Elegido",

  "setup.finish.title": "Todo listo",
  "setup.finish.sub": "Esto es lo que tenemos. Todo se puede cambiar en Ajustes.",
  "setup.finish.agency": "Agencia",
  "setup.finish.lines": "Líneas",
  "setup.finish.carriers": "Aseguradoras / MGAs",
  "setup.finish.forms": "Proveedor de formularios",
  "setup.finish.texting": "Mensajes de texto",
  "setup.finish.data": "Datos iniciales",
  "setup.finish.open": "Abrir el espacio de trabajo",
  "setup.finish.guide": "Iniciar la demo guiada",
  "setup.finish.none": "Sin definir",
  "setup.finish.texting.on": "Sí",
  "setup.finish.texting.off": "Por ahora no",

  "guide.title": "Demo guiada",
  "guide.subtitle": "El recorrido principal de principio a fin. Cada paso abre la pantalla donde ocurre.",
  "guide.progress": "{done} de {total} listos",
  "guide.open": "Abrir",
  "guide.markDone": "Marcar listo",
  "guide.undo": "Deshacer",
  "guide.doneAt": "Listo",
  "guide.step.setup": "Iniciar sesión y completar la configuración",
  "guide.step.setup.sub": "Identidad, perfil del negocio, conexiones y datos iniciales. Se puede retomar en cualquier momento.",
  "guide.step.import": "Importar un archivo de muestra de clientes / pólizas",
  "guide.step.import.sub": "Subir → ver filas → asignar columnas → validar → revisar → importar. Se incluyen dos exportaciones de muestra con estructuras distintas.",
  "guide.step.customer": "Abrir un cliente importado",
  "guide.step.customer.sub": "Busque por nombre, teléfono o número de póliza; vea pólizas, documentos, conversación y tareas en una sola página.",
  "guide.step.faraday": "Pedir a Faraday que prepare un formulario y liste lo que falta",
  "guide.step.faraday.sub": "En el panel de Faraday escriba: preparar 25 para <nombre del cliente>. Faraday inicia el borrador e informa las preguntas sin responder.",
  "guide.step.message": "Intercambiar un mensaje de prueba",
  "guide.step.message.sub": "Texto real a un participante en la lista permitida cuando los mensajes estén conectados; si no, una conversación simulada claramente identificada.",
  "guide.step.answer": "Guardar la respuesta recibida",
  "guide.step.answer.sub": "Ponga la respuesta del cliente en el borrador del formulario. Lo desconocido sigue desconocido; nada se inventa.",
  "guide.step.pdf": "Generar, ver y descargar el PDF de preparación",
  "guide.step.pdf.sub": "Un documento original con la marca de la agencia, marcado DEMO, guardado en el historial de documentos del cliente.",
  "guide.step.carrier": "Ejecutar una respuesta de aseguradora de demostración",
  "guide.step.carrier.sub": "El simulador pide un dato faltante y luego produce un archivo de póliza de muestra.",
  "guide.step.carrierImport": "Procesar ese archivo con la importación real",
  "guide.step.carrierImport.sub": "La misma asignación y validación que cualquier carga; los cambios de póliza y las tareas aparecen en el CRM.",
  "guide.step.review": "Mostrar el cliente, documento, conversación y tarea actualizados",
  "guide.step.review.sub": "Todo lo que hizo Faraday o el simulador está en la página del cliente con su recibo.",
  "guide.step.persist": "Recargar y confirmar que el trabajo persiste",
  "guide.step.persist.sub": "Recargue el navegador o vuelva a iniciar sesión. Los registros viven en el servidor, no en la pestaña.",
  "guide.explore": "Exploración libre",
  "guide.exploreSub": "Todo lo demás está abierto. Vuelva aquí cuando quiera desde el menú.",
  "guide.tip": "Consejo",

  "settings.agency": "Agencia",
  "settings.agency.sub": "Lo que se capturó durante la configuración.",
  "settings.agency.edit": "Editar en configuración",
  "settings.agency.notSet": "Sin definir",
  "settings.agency.fictional": "Agencia ficticia de demostración",
  "settings.state": "Estado",
  "settings.configured": "Configurado",
  "settings.running": "En uso",
  "settings.staff": "Personal y productores",
  "settings.staff.sub": "Roles, idiomas, NPN de productor y licencias estatales viven aquí, no en la agencia.",
  "settings.staff.add": "Agregar miembro",
  "settings.staff.edit": "Editar",
  "settings.staff.name": "Nombre",
  "settings.staff.email": "Correo",
  "settings.staff.role": "Rol",
  "settings.staff.title": "Puesto",
  "settings.staff.phone": "Teléfono",
  "settings.staff.npn": "NPN del productor",
  "settings.staff.licenses": "Licencias estatales",
  "settings.staff.active": "Activo",
  "settings.staff.inactive": "Inactivo",
  "settings.staff.saved": "Miembro guardado",
  "settings.activation": "Activación",
  "settings.activation.sub": "Diga a 10x qué conexiones quiere encender. Esto registra el interés y los pendientes de configuración; no compra ni activa nada.",
  "settings.activation.request": "Solicitar activación",
  "settings.activation.items": "Conexiones a activar",
  "settings.activation.notes": "Notas",
  "settings.activation.contact": "Su nombre",
  "settings.activation.contactEmail": "Correo",
  "settings.activation.contactPhone": "Teléfono",
  "settings.activation.sent": "Solicitud de activación registrada",
  "settings.activation.past": "Solicitudes",
  "settings.activation.remaining": "Configuración pendiente",
  "settings.activation.none": "Aún no hay solicitudes.",
  "settings.demoData": "Datos de demostración",
  "settings.demoData.sub": "Los registros de muestra están etiquetados y separados de lo que usted capture. Quitarlos o recargarlos nunca toca sus clientes, mensajes ni documentos.",
  "settings.demoData.loaded": "Libro de muestra cargado",
  "settings.demoData.notLoaded": "Sin libro de muestra",
  "settings.demoData.counts": "{customers} clientes ({sample} de muestra) · {policies} pólizas",
  "settings.demoData.reload": "Recargar libro de muestra",
  "settings.demoData.remove": "Quitar registros de muestra",
  "settings.demoData.done": "Datos de muestra actualizados",
  "settings.importLink": "Importar clientes y pólizas",
  "settings.importLink.sub": "Suba una exportación de su sistema actual, asigne las columnas, valide e importe.",
  "settings.importLink.open": "Abrir Importar",
};
